export default function ParanoidiAmbientBackground({ intensity = "normal" }) {
  const isSoft = intensity === "soft";

  const sparks = [
    { left: "10%", top: "78%", drift: "4vw", delay: "0s", duration: "10s" },
    { left: "22%", top: "62%", drift: "-3vw", delay: "-2s", duration: "12s" },
    { left: "34%", top: "42%", drift: "3vw", delay: "-5s", duration: "11s" },
    { left: "48%", top: "86%", drift: "-4vw", delay: "-3s", duration: "13s" },
    { left: "61%", top: "54%", drift: "4vw", delay: "-7s", duration: "12s" },
    { left: "76%", top: "34%", drift: "-3vw", delay: "-4s", duration: "14s" },
    { left: "88%", top: "70%", drift: "3vw", delay: "-8s", duration: "11s" },
  ];

  return (
    <div
      aria-hidden="true"
      className={[
        "pointer-events-none fixed inset-0 z-[1] overflow-hidden",
        isSoft ? "opacity-45" : "opacity-65",
      ].join(" ")}
    >
      <style>
        {`
          @keyframes paranoidiGlowBreath {
            0%, 100% {
              opacity: 0.34;
              transform: scale(1);
            }

            50% {
              opacity: 0.66;
              transform: scale(1.08);
            }
          }

          @keyframes paranoidiFlameCenter {
            0%, 100% {
              transform: translate3d(-50%, 4%, 0) scaleX(0.98) scaleY(0.92);
              opacity: 0.34;
            }

            50% {
              transform: translate3d(-50%, -5%, 0) scaleX(1.08) scaleY(1.14);
              opacity: 0.72;
            }
          }

          @keyframes paranoidiFlameSideLeft {
            0%, 100% {
              transform: translate3d(-2%, 5%, 0) scaleX(0.95) scaleY(0.9) rotate(-4deg);
              opacity: 0.28;
            }

            50% {
              transform: translate3d(3%, -4%, 0) scaleX(1.08) scaleY(1.12) rotate(3deg);
              opacity: 0.58;
            }
          }

          @keyframes paranoidiFlameSideRight {
            0%, 100% {
              transform: translate3d(2%, 5%, 0) scaleX(0.96) scaleY(0.92) rotate(4deg);
              opacity: 0.24;
            }

            50% {
              transform: translate3d(-3%, -3%, 0) scaleX(1.08) scaleY(1.1) rotate(-3deg);
              opacity: 0.52;
            }
          }

          @keyframes paranoidiSparkFloat {
            0% {
              transform: translate3d(0, 0, 0) scale(0.45);
              opacity: 0;
            }

            12% {
              opacity: 0.78;
            }

            58% {
              opacity: 0.42;
            }

            100% {
              transform: translate3d(var(--spark-drift), -42vh, 0) scale(1);
              opacity: 0;
            }
          }

          @keyframes paranoidiHeatMove {
            0%, 100% {
              transform: translate3d(-2%, 0, 0) skewX(-5deg);
              opacity: 0.12;
            }

            50% {
              transform: translate3d(2%, -2%, 0) skewX(5deg);
              opacity: 0.28;
            }
          }

          .paranoidi-ambient-base {
            position: absolute;
            inset: 0;
            background:
              radial-gradient(circle at 15% 22%, rgba(239, 68, 68, 0.16), transparent 28rem),
              radial-gradient(circle at 86% 26%, rgba(185, 28, 28, 0.13), transparent 30rem),
              radial-gradient(circle at 50% 52%, rgba(239, 68, 68, 0.10), transparent 34rem),
              radial-gradient(circle at 18% 88%, rgba(239, 68, 68, 0.20), transparent 30rem),
              radial-gradient(circle at 82% 92%, rgba(180, 35, 24, 0.20), transparent 32rem),
              radial-gradient(circle at 50% 112%, rgba(250, 204, 21, 0.10), transparent 36rem);
          }

          .paranoidi-ambient-wash {
            position: absolute;
            border-radius: 9999px;
            filter: blur(62px);
            mix-blend-mode: screen;
            animation: paranoidiGlowBreath 8s ease-in-out infinite;
          }

          .paranoidi-ambient-wash-top {
            left: -10rem;
            top: 4rem;
            width: 32rem;
            height: 24rem;
            background: radial-gradient(circle, rgba(239,68,68,0.22), transparent 70%);
          }

          .paranoidi-ambient-wash-mid {
            right: -12rem;
            top: 34%;
            width: 34rem;
            height: 26rem;
            background: radial-gradient(circle, rgba(185,28,28,0.20), transparent 72%);
            animation-delay: -3s;
          }

          .paranoidi-ambient-wash-center {
            left: 50%;
            top: 48%;
            width: 36rem;
            height: 24rem;
            transform: translateX(-50%);
            background: radial-gradient(circle, rgba(239,68,68,0.12), transparent 72%);
            animation-delay: -5s;
          }

          .paranoidi-ambient-glow {
            position: absolute;
            left: 50%;
            bottom: -16rem;
            width: 72rem;
            height: 34rem;
            border-radius: 9999px;
            background:
              radial-gradient(circle, rgba(239,68,68,0.30), rgba(127,29,29,0.10) 44%, transparent 72%);
            filter: blur(54px);
            transform: translateX(-50%);
            animation: paranoidiGlowBreath 6s ease-in-out infinite;
          }

          .paranoidi-flame {
            position: absolute;
            bottom: -15rem;
            border-radius: 9999px 9999px 42% 42%;
            filter: blur(46px);
            mix-blend-mode: screen;
            transform-origin: bottom center;
            will-change: transform, opacity;
          }

          .paranoidi-flame-left {
            left: -8rem;
            width: 25rem;
            height: 35rem;
            background:
              radial-gradient(ellipse at 50% 72%, rgba(239,68,68,0.34), transparent 68%),
              radial-gradient(ellipse at 50% 96%, rgba(250,204,21,0.10), transparent 62%);
            animation: paranoidiFlameSideLeft 4.8s ease-in-out infinite;
          }

          .paranoidi-flame-center {
            left: 50%;
            width: 34rem;
            height: 40rem;
            background:
              radial-gradient(ellipse at 50% 70%, rgba(239,68,68,0.34), transparent 68%),
              radial-gradient(ellipse at 50% 96%, rgba(250,204,21,0.13), transparent 62%);
            animation: paranoidiFlameCenter 5.4s ease-in-out infinite;
            animation-delay: -1.8s;
          }

          .paranoidi-flame-right {
            right: -9rem;
            width: 25rem;
            height: 35rem;
            background:
              radial-gradient(ellipse at 50% 72%, rgba(185,28,28,0.32), transparent 68%),
              radial-gradient(ellipse at 50% 96%, rgba(248,113,113,0.10), transparent 62%);
            animation: paranoidiFlameSideRight 5.1s ease-in-out infinite;
            animation-delay: -2.8s;
          }

          .paranoidi-heat {
            position: absolute;
            left: 8%;
            right: 8%;
            bottom: -7rem;
            height: 34rem;
            background:
              linear-gradient(94deg, transparent, rgba(255,255,255,0.026), transparent),
              linear-gradient(110deg, transparent, rgba(239,68,68,0.07), transparent);
            filter: blur(20px);
            mix-blend-mode: screen;
            animation: paranoidiHeatMove 5.5s ease-in-out infinite;
          }

          .paranoidi-spark {
            position: absolute;
            top: var(--spark-top);
            left: var(--spark-left);
            width: 3px;
            height: 3px;
            border-radius: 9999px;
            background: rgba(255, 232, 190, 0.9);
            box-shadow:
              0 0 8px rgba(255,255,255,0.42),
              0 0 14px rgba(248,113,113,0.72),
              0 0 22px rgba(250,204,21,0.22);
            animation: paranoidiSparkFloat var(--spark-duration) linear infinite;
            animation-delay: var(--spark-delay);
            will-change: transform, opacity;
          }

          .paranoidi-spark::after {
            content: "";
            position: absolute;
            left: 50%;
            top: 50%;
            width: 1px;
            height: 18px;
            transform: translate(-50%, -14px);
            border-radius: 9999px;
            background: linear-gradient(180deg, transparent, rgba(248,113,113,0.55), transparent);
          }

          @media (max-width: 640px) {
            .paranoidi-ambient-wash-top {
              left: -14rem;
              top: 5rem;
            }

            .paranoidi-ambient-wash-mid {
              right: -16rem;
              top: 38%;
            }

            .paranoidi-ambient-glow {
              width: 44rem;
              height: 28rem;
              bottom: -15rem;
            }

            .paranoidi-flame {
              bottom: -16rem;
              filter: blur(50px);
            }

            .paranoidi-flame-left,
            .paranoidi-flame-right {
              width: 20rem;
              height: 30rem;
            }

            .paranoidi-flame-center {
              width: 26rem;
              height: 34rem;
            }

            .paranoidi-spark {
              width: 2px;
              height: 2px;
            }
          }

          @media (prefers-reduced-motion: reduce) {
            .paranoidi-ambient-wash,
            .paranoidi-ambient-glow,
            .paranoidi-flame,
            .paranoidi-heat,
            .paranoidi-spark {
              animation: none;
            }
          }
        `}
      </style>

      <div className="paranoidi-ambient-base" />

      <div className="paranoidi-ambient-wash paranoidi-ambient-wash-top" />
      <div className="paranoidi-ambient-wash paranoidi-ambient-wash-mid" />
      <div className="paranoidi-ambient-wash paranoidi-ambient-wash-center" />

      <div className="paranoidi-ambient-glow" />

      <div className="paranoidi-flame paranoidi-flame-left" />
      <div className="paranoidi-flame paranoidi-flame-center" />
      <div className="paranoidi-flame paranoidi-flame-right" />
      <div className="paranoidi-heat" />

      {sparks.map((spark, index) => (
        <span
          key={index}
          className="paranoidi-spark"
          style={{
            "--spark-left": spark.left,
            "--spark-top": spark.top,
            "--spark-drift": spark.drift,
            "--spark-delay": spark.delay,
            "--spark-duration": spark.duration,
          }}
        />
      ))}
    </div>
  );
}